import { createContext, type ReactNode, useCallback, useContext } from 'react'
import { Bookmark, MessageSquare } from 'lucide-react'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import { childrenToText } from '@/lib/markdown-code-block'
import { findLineRangeForExcerpt } from '@/lib/markdown-line-range'
import { clipPasteReferenceExcerpt, clipPasteReferenceTail } from '@/components/ui/contenteditable-chat-input.utils'
import { captureDocumentScrollRatio } from '@/lib/document-scroll-restore'
import {
  nearestHeadingIdForRatio,
  setSoftBookmark,
} from '@/lib/document-soft-bookmark'
import { extractHeadings, computeHeadingRatios } from '@/lib/heading-utils'

/** Bookmarked heading id - kept out of ReactMarkdown `components` so bookmark updates do not remount the doc (scroll jump). */
export const PreviewBookmarkedHeadingContext = createContext<string | null>(null)

type PreviewReadingGutterProps = {
  children: ReactNode
  tag: 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'blockquote' | 'pre' | 'li' | 'div'
  className?: string
  id?: string
  documentId?: string | null
  documentTitle?: string | null
  documentPath?: string | null
  markdownSource?: string
  showAddToChat?: boolean
  showBookmark?: boolean
}

function dispatchAddToChat(detail: {
  id: string
  title: string
  path?: string
  documentType?: string
  lineStart?: number
  lineEnd?: number
  excerpt?: string
  excerptTail?: string
}): void {
  window.dispatchEvent(new CustomEvent('add-to-chat', { detail }))
}

/**
 * Block wrapper for preview mode: optional bookmark (left) and add-to-chat (right).
 * Callers should pass showBookmark only for H1-H4 - body blocks (p/blockquote/li/pre)
 * nested under quotes otherwise stacked duplicate left icons.
 */
export function PreviewReadingGutter({
  children,
  tag: Tag,
  className,
  id,
  documentId,
  documentTitle,
  documentPath,
  markdownSource = '',
  showAddToChat = false,
  showBookmark = true,
}: PreviewReadingGutterProps) {
  const excerpt = childrenToText(children).trim()
  const bookmarkedHeadingId = useContext(PreviewBookmarkedHeadingContext)
  const isBookmarked = Boolean(id) && bookmarkedHeadingId != null && bookmarkedHeadingId === id

  const handleAddToChat = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!documentId) return
      const lines = findLineRangeForExcerpt(markdownSource, excerpt)
      dispatchAddToChat({
        id: documentId,
        title: documentTitle || documentPath?.split('/').pop() || 'Document',
        path: documentPath || documentId,
        documentType: 'note',
        lineStart: lines.lineStart,
        lineEnd: lines.lineEnd,
        excerpt: clipPasteReferenceExcerpt(excerpt),
        excerptTail: clipPasteReferenceTail(excerpt) || undefined,
      })
    },
    [documentId, documentTitle, documentPath, markdownSource, excerpt]
  )

  const handleBookmark = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!documentId) return
      const scrollRoot = (e.currentTarget as HTMLElement).closest(
        '[data-scroll-container]'
      ) as HTMLElement | null
      const contentRoot =
        (scrollRoot?.querySelector('.document-reading-prose') as HTMLElement | null) ||
        scrollRoot
      // Preserve scroll across the soft-bookmark state update (belt-and-suspenders).
      const scrollTopBefore = scrollRoot?.scrollTop ?? null
      const ratio = captureDocumentScrollRatio(scrollRoot) ?? 0
      let headingId: string | null = id ?? null
      if (!headingId && contentRoot && scrollRoot) {
        const headings = extractHeadings(markdownSource)
        computeHeadingRatios(headings, contentRoot, scrollRoot)
        headingId = nearestHeadingIdForRatio(headings, ratio)
      }
      setSoftBookmark(documentId, { ratio, headingId })
      if (scrollRoot && scrollTopBefore != null) {
        requestAnimationFrame(() => {
          scrollRoot.scrollTop = scrollTopBefore
        })
      }
    },
    [documentId, id, markdownSource]
  )

  const showLeft = showBookmark
  const showRight = showAddToChat

  return (
    <Tag
      id={id}
      className={cn(
        'group/reading-block relative',
        // Hit area extends into gutters via absolute icons - do not pad the block
        // (padding here was shifting preview text vs TipTap edit mode).
        className
      )}
      data-testid={testIds.markdownPreview.readingBlock}
    >
      {showLeft && (
        <span
          className={cn(
            'print-hide absolute inset-y-0 left-0 z-10 flex w-11 -translate-x-full items-start justify-center pt-0.5',
            'opacity-0 transition-opacity duration-100',
            'group-hover/reading-block:opacity-100',
            isBookmarked && 'opacity-100'
          )}
          data-testid={testIds.markdownPreview.readingGutter}
          onDoubleClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <button
            type="button"
            title={isBookmarked ? 'Update bookmark' : 'Bookmark here'}
            aria-label={isBookmarked ? 'Update bookmark' : 'Bookmark here'}
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-md',
              'text-muted-foreground hover:text-foreground hover:bg-accent',
              isBookmarked && 'text-primary'
            )}
            data-testid={testIds.markdownPreview.bookmarkButton}
            onClick={handleBookmark}
          >
            <Bookmark className={cn('h-3.5 w-3.5', isBookmarked && 'fill-current')} />
          </button>
        </span>
      )}
      {showRight && (
        <span
          className={cn(
            'print-hide absolute inset-y-0 right-0 z-10 flex w-11 translate-x-full items-start justify-center pt-0.5',
            'opacity-0 transition-opacity duration-100',
            'group-hover/reading-block:opacity-100'
          )}
          data-testid={testIds.markdownPreview.readingGutterChat}
          onDoubleClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <button
            type="button"
            title="Add to chat"
            aria-label="Add to chat"
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-md',
              'text-muted-foreground hover:text-foreground hover:bg-accent'
            )}
            data-testid={testIds.markdownPreview.addToChatButton}
            onClick={handleAddToChat}
          >
            <MessageSquare className="h-3.5 w-3.5" />
          </button>
        </span>
      )}
      {children}
    </Tag>
  )
}
